import "./stores/auth.store";
import "./index.css";

import {
  init as sentryInit,
  reactErrorHandler,
  replayIntegration,
} from "@sentry/react";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

import App from "./App";
import { BrowserRouter } from "react-router-dom";

import { Provider } from "react-redux";
import ReactDOM from "react-dom/client";
import { SENTRY } from "./utils/constants";
import { getAppBuildVersion, getPlatform } from "./utils/config";
import { ServiceWorkerUpdateAvailable } from "./components/ServiceWorkerUpdateAvailable";
import { store } from "./redux/redux";
import "./utils/otplessInitializer";
import ErrorBoundary from "ErrorBoundary";

// SPA owns scroll. Disable the browser's deferred scroll restoration — on
// back/forward it keeps re-applying the old offset while async content is
// still loading, which visibly scrolls the page and fights in-app restores
// (e.g. sourcing PLP ↔ PDP).
if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

if (process.env.NODE_ENV === "production") {
  sentryInit({
    dsn: SENTRY.DSN,
    tracesSampleRate: SENTRY.SAMPLE_RATE,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    environment: process.env.NODE_ENV,
    replaysOnErrorSampleRate: 1,
    replaysSessionSampleRate: 0.1,
    autoSessionTracking: true,
    integrations: [
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    ignoreErrors: [
      /SyntaxError: JSON Parse error: Unterminated string/, // ignore blitz sdk internal parsing error
      /originalEvent\.origin/, // ignore freshdesk widget errors
      /originalEvent is undefined/, // ignore freshdesk widget errors
    ],
    beforeSend(event, hint) {
      // Filter out errors from Freshdesk widget
      const error = hint?.originalException;
      const errorString = error?.toString() || "";
      const stackString = error?.stack || "";

      // Ignore if error is from freshdesk domain
      if (
        stackString.includes("freshdesk.com") ||
        stackString.includes("freshchat.com") ||
        errorString.includes("freshdesk") ||
        errorString.includes("freshchat")
      ) {
        return null;
      }

      // Ignore if error mentions originalEvent
      if (errorString.includes("originalEvent")) {
        return null;
      }

      return event;
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"), {
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: reactErrorHandler(),
});

const serviceWorkerModalRoot = ReactDOM.createRoot(
  document.getElementById("service-worker-modal-root")
);

const render = () => {
  root.render(
    <Provider store={store}>
      <BrowserRouter>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </Provider>
  );
};

const ServiceWorkerModal = () => {
  serviceWorkerModalRoot.render(<ServiceWorkerUpdateAvailable />);
};
ServiceWorkerModal();

window.customCopy = (text, navigator) => {
  if (window.ReactNativeWebView) {
    window?.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: "copy", payload: text })
    );
  } else {
    navigator?.clipboard?.writeText(text);
  }
};

// Holds whichever caller most recently requested a paste — the message listeners below are
// registered once (not per-call), so they always dispatch to the latest pasteFunction rather
// than accumulating a new listener (and a new stale closure) on every customPaste() call.
let currentPasteFunction = null;
let pasteListenerRegistered = false;

window.customPaste = (pasteFunction, navigator) => {
  if (window?.ReactNativeWebView) {
    currentPasteFunction = pasteFunction;
    window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: "paste" }));
    if (!pasteListenerRegistered) {
      pasteListenerRegistered = true;
      if (getPlatform() === "android") {
        document.addEventListener("message", (e) => {
          try {
            const response = JSON.parse(e.data);
            // Other bridge messages (e.g. fetch_og_data_result) share this channel —
            // without this check, currentPasteFunction gets called with an unrelated
            // payload shape (an object, not clipboard text), and callers doing
            // `clipText.replace(...)` crash on it.
            if (response?.type !== "paste") return;
            currentPasteFunction?.(response?.payload || "");
          } catch (error) {
            // eslint-disable-next-line
          }
        });
      } else {
        window.addEventListener("message", (e) => {
          if (!e?.data) return;
          try {
            const response = JSON.parse(e.data);
            if (response?.type !== "paste") return;
            if (response && typeof response === "object") {
              currentPasteFunction?.(response?.payload || "");
            }
          } catch (error) {
            console.error(
              "Failed to parse message data :: ios :: paste :",
              error
            );
          }
        });
      }
    }
  } else {
    navigator?.clipboard?.readText()?.then(pasteFunction);
  }
};
window.customShare = (text, navigator) => navigator?.share({ text: text });

let ogBridgeRequestCounter = 0;
const ogBridgePending = new Map();
let ogBridgeListenerRegistered = false;
// Deliberately longer than native's own TIMEOUT_MS (3000, see ogFetch.ts in
// wishlink-creator-react-native) so native is always the side that gives up first and replies
// with an error we can act on immediately — not a second, competing deadline that abandons
// work native is still doing. With the build gate below excluding the builds that never reply
// at all, this is now only a safety net for a reply lost to a mid-flight navigation.
const OG_BRIDGE_TIMEOUT_MS = 3600;

// First app build shipping the fetch_og_data handler (APP_BUILD in wishlink-creator-react-native's
// src/constants/index.ts). Older builds ignore the message and never reply, so without this gate
// every single link add on them burns OG_BRIDGE_TIMEOUT_MS before falling back to backend
// scraping — on a bulk paste, once per OG_BRIDGE_CONCURRENCY-sized batch.
const OG_BRIDGE_MIN_APP_BUILD = 57;

// On a bulk paste, every link would otherwise fire customFetchOgData at once. Native serves
// only MAX_CONCURRENT of them at a time and queues the rest, so the surplus calls would burn
// their OG_BRIDGE_TIMEOUT_MS sitting in that queue and resolve null without ever having been
// attempted. Gating admission here means a call's timeout clock only starts once native
// actually has capacity for it.
//
// Must stay at or below native's MAX_CONCURRENT (6) for that to hold. Held equal to it: the
// web admits exactly what native can serve, so native's queue stays empty and no request pays
// for time it spent waiting rather than fetching.
const OG_BRIDGE_CONCURRENCY = 6;
let ogBridgeInFlight = 0;
const ogBridgeWaitQueue = [];

function acquireOgBridgeSlot() {
  if (ogBridgeInFlight < OG_BRIDGE_CONCURRENCY) {
    ogBridgeInFlight++;
    return Promise.resolve();
  }
  return new Promise((resolve) => ogBridgeWaitQueue.push(resolve));
}

function releaseOgBridgeSlot() {
  const next = ogBridgeWaitQueue.shift();
  if (next) {
    next();
  } else {
    ogBridgeInFlight--;
  }
}

const handleFetchOgDataMessage = (e) => {
  let parsed;
  try {
    parsed = JSON.parse(e.data);
  } catch {
    return;
  }
  if (parsed?.type !== "fetch_og_data_result") return;
  const entry = ogBridgePending.get(parsed?.payload?.requestId);
  if (!entry) return;
  clearTimeout(entry.timer);
  ogBridgePending.delete(parsed.payload.requestId);
  entry.resolve(parsed.payload);
};

// Read-only view of the string native signed. Parsed for local use only — the string itself is
// forwarded to the backend untouched, which verifies these exact bytes before parsing them
// (og_fetch.py:verify_client_og_attestation), so re-serializing it here would break the MAC.
const parseAttestedOg = (signedPayload) => {
  try {
    const parsed = JSON.parse(signedPayload);
    return parsed?.og ? parsed : null;
  } catch {
    return null;
  }
};

// Best-effort client-side OG tag fetch + shortlink expansion via the native app's hidden
// WebView (see wishlink-creator-react-native's fetch_og_data bridge). Never throws — resolves
// null on any failure so callers need no try/catch. Falls back to backend scraping when this
// returns null (old app build, non-RN browser, page load failure, or timeout).
window.customFetchOgData = async (url, { needsVariants } = {}) => {
  try {
    if (!window?.ReactNativeWebView) {
      return null;
    }

    // Reads appBuild off the injected object, which every build has shipped for years, and
    // falls back to "newest" if it can't — so a parse failure costs a timeout, never the
    // feature on a build that supports it.
    if (getAppBuildVersion() < OG_BRIDGE_MIN_APP_BUILD) {
      return null;
    }

    if (!ogBridgeListenerRegistered) {
      if (getPlatform() === "android") {
        document.addEventListener("message", handleFetchOgDataMessage);
      } else {
        window.addEventListener("message", handleFetchOgDataMessage);
      }
      ogBridgeListenerRegistered = true;
    }

    // Same timeout every call. The build gate above already excludes the only case that
    // never replies, so a shorter "probe" timeout would have nothing left to detect and
    // would only misfire on a genuinely slow page load.
    const requestId = `og_${Date.now()}_${ogBridgeRequestCounter++}`;

    await acquireOgBridgeSlot();
    let payload;
    try {
      payload = await new Promise((resolve) => {
        const timer = setTimeout(() => {
          ogBridgePending.delete(requestId);
          resolve(null);
        }, OG_BRIDGE_TIMEOUT_MS);
        ogBridgePending.set(requestId, { resolve, timer });
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "fetch_og_data",
            payload: { requestId, url, needsVariants: !!needsVariants },
          })
        );
      });
    } finally {
      releaseOgBridgeSlot();
    }

    if (!payload) return null;
    // Native sends the OG data once, not twice: inside the signed string when it could sign,
    // flat when it couldn't (older build with no signPayload, native HMAC error). Unpack the
    // signed copy for local use — the gate below and the sourcing forms' client-side prefill
    // both read these fields — while forwarding attestation itself byte-for-byte.
    const signed = payload.attestation
      ? parseAttestedOg(payload.attestation.payload)
      : null;
    const expandedUrl = signed ? signed.expandedUrl : payload.expandedUrl;
    const og = signed ? signed.og : payload.og;

    // A resolved URL is useful on its own (skips backend's own shortlink-expansion step)
    // even when OG/JSON-LD extraction came back thin (e.g. a bot-check page with no image) —
    // don't discard it just because the fuller product-card fields are missing. Symmetrically,
    // a rich result (title+image) for a non-redirecting URL has no expandedUrl at all — don't
    // discard that either. The backend separately gates the full "build a card from this" path
    // on title+image being present.
    if (!expandedUrl && !(og?.title && og?.image)) return null;

    return {
      // Native's own signature over the exact bytes it fetched (see ogFetch.ts:signOgPayload).
      // Forward it untouched — the backend verifies the string before parsing it, so anything
      // that re-serializes it invalidates the signature and the whole payload is dropped.
      // Absent on older app builds, where the backend falls back to header-only trust.
      attestation: payload.attestation,
      expanded_url: expandedUrl,
      title: og?.title,
      description: og?.description,
      image: og?.image,
      site_name: og?.siteName,
      price: og?.price,
      currency: og?.currency,
      brand: og?.brand,
      sku: og?.sku,
      all_images: og?.allImages,
      rating: og?.rating,
      num_ratings: og?.numRatings,
      is_oos: og?.isOos,
      original_price: og?.originalPrice,
      variants: og?.variants,
      available_sizes: og?.availableSizes,
      available_colors: og?.availableColors,
      base_color: og?.baseColor,
    };
  } catch (error) {
    console.warn("customFetchOgData failed:", error);
    return null;
  }
};

render();

function registerServiceWorker() {
  serviceWorkerRegistration.register({
    onSuccess: () => {
      console.log("Service Worker registered successfully");
    },
    onUpdate: (registration) => {
      console.log("update available");
    },
  });
}

// Call this function where appropriate in your app initialization
if (window?.ReactNativeWebView) {
  registerServiceWorker();
}
